/**
 * Saved Candidates Page
 *
 * Displays recruiter's saved/favorited candidates.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useListSavedCandidatesQuery,
  useUnsaveCandidateMutation,
} from '../store/api/recruiterApi';
import { LoadErrorNotice } from '../components/LoadErrorNotice';

export function SavedCandidatesPage() {
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data, isLoading, isError, error, refetch } = useListSavedCandidatesQuery({
    limit,
    offset: page * limit,
  });
  const [unsaveCandidate, { isLoading: isUnsaving }] = useUnsaveCandidateMutation();

  const handleUnsave = async (candidateUserId: string) => {
    if (confirm('Remove this candidate from your saved list?')) {
      try {
        await unsaveCandidate(candidateUserId).unwrap();
        refetch();
      } catch (err) {
        console.error('Failed to unsave candidate:', err);
      }
    }
  };

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saved Candidates</h1>
          <p className="mt-1 text-gray-500">
            {data
              ? `${data.total} candidate${data.total !== 1 ? 's' : ''} shortlisted`
              : 'Candidates you save from the Marketplace'}
          </p>
        </div>
        <Link to="/marketplace" className="btn btn-primary">
          Browse Marketplace
        </Link>
      </div>

      {/* Candidates Grid */}
      <div className="card">
        <div className="card-body">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : isError ? (
            // A failed load is not "no candidates shortlisted yet".
            <LoadErrorNotice
              error={error}
              onRetry={() => void refetch()}
              failedMessage="Your saved candidates could not be loaded."
              testIdPrefix="saved-candidates"
            />
          ) : data && data.items.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.items.map((candidate) => (
                <div
                  key={candidate.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    {candidate.avatarUrl ? (
                      <img
                        src={candidate.avatarUrl}
                        alt={candidate.displayName || 'Candidate'}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="font-medium text-primary-600 text-lg">
                          {candidate.displayName?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {candidate.displayName || 'Unknown'}
                      </h3>
                      {candidate.headline && (
                        <p className="text-sm text-gray-500 truncate">{candidate.headline}</p>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  {candidate.note && (
                    <p className="mt-3 text-sm text-gray-600 line-clamp-2 italic">
                      "{candidate.note}"
                    </p>
                  )}

                  {/* Tags */}
                  {candidate.tags && candidate.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {candidate.tags.map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Meta + Actions */}
                  <div className="mt-3 pt-3 border-t flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      Saved {new Date(candidate.savedAt).toLocaleDateString()}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleUnsave(candidate.candidateUserId)}
                        disabled={isUnsaving}
                        className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove from shortlist"
                      >
                        Remove
                      </button>
                      {candidate.publicSlug && (
                        <Link
                          to={`/u/${candidate.publicSlug}`}
                          className="btn btn-sm btn-primary text-xs px-3"
                        >
                          View Profile
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <HeartIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No candidates shortlisted yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Browse the marketplace and save candidates you want to evaluate
              </p>
              <Link to="/marketplace" className="btn btn-primary mt-4">
                Find Talent
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="btn btn-sm btn-outline"
          >
            Previous
          </button>
          <span className="py-2 px-4 text-sm text-gray-600">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="btn btn-sm btn-outline"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function HeartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
      />
    </svg>
  );
}
